import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, UserPlus, PackageCheck, RefreshCw, AlertTriangle, Clock, ThumbsUp, RotateCcw, BarChart3, PartyPopper } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LangContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const ICONS = { assigned: UserPlus, delivered: PackageCheck, status: RefreshCw, overdue: AlertTriangle, reminder: Clock, approved: ThumbsUp, revision: RotateCcw, digest: BarChart3, deadline: Clock, event: PartyPopper };
const TINT = {
  reminder: "text-amber-300 bg-amber-500/15",
  approved: "text-emerald-300 bg-emerald-500/15",
  revision: "text-amber-300 bg-amber-500/15",
  assigned: "text-indigo-300 bg-indigo-500/15",
  delivered: "text-emerald-300 bg-emerald-500/15",
  status: "text-blue-300 bg-blue-500/15",
  overdue: "text-rose-300 bg-rose-500/15",
};

export default function NotificationBell({ variant = "desktop" }) {
  const { t } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  const openItem = async (n) => {
    if (!n.read) await api.patch(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.link) nav(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={variant === "mobile" ? "notification-bell-mobile" : "notification-bell"} className="relative w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/5 text-slate-300">
          <Bell className="w-4.5 h-4.5 w-[18px] h-[18px]" />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 bg-slate-900 border-white/10 text-slate-100" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <div className="font-semibold text-sm" style={{ fontFamily: "Outfit" }}>{t("notifications")}</div>
          {unread > 0 && (
            <button data-testid="notification-read-all" onClick={readAll} className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> {t("mark_all_read")}
            </button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {items.length === 0 && <div className="text-sm text-slate-500 text-center py-10">{t("no_notifications")}</div>}
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 flex gap-3 border-b border-white/5 hover:bg-white/5 ${n.read ? "opacity-60" : ""}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${TINT[n.type] || "bg-white/5"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {n.title}
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{n.message}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
